import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getSEOConfig, getCanonicalUrl, SITE_URL } from '../config/seo';
import { pulseArticlesSorted } from '../content/pulse';
import { pressReleasesSorted } from '../content/press';

const DEFAULT_OG_IMAGE = `${SITE_URL}/sns-focus-logo.png`;

const Seo: React.FC = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const seoConfig = getSEOConfig(pathname);
  const canonicalUrl = getCanonicalUrl(pathname);
  const ogImage = seoConfig.ogImage || DEFAULT_OG_IMAGE;

  const isPublicPage = !seoConfig.noindex;
  const isArticlePage = Boolean(seoConfig.articleMeta);
  const ogType = isArticlePage ? 'article' : 'website';

  // Site-wide JSON-LD (only on public pages)
  const organizationSchema = isPublicPage
    ? {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'SNS Intelligence',
        url: SITE_URL,
        email: 'info@snsintel.com',
        logo: DEFAULT_OG_IMAGE,
        parentOrganization: {
          '@type': 'Organization',
          name: 'SNS Research',
        },
      }
    : null;

  const websiteSchema = isPublicPage
    ? {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'SNS Intelligence',
        url: SITE_URL,
      }
    : null;

  // Page-specific structured data
  let pageSpecificSchema: Record<string, unknown> | null = null;
  if (isPublicPage) {
    if (isArticlePage && seoConfig.articleMeta) {
      const meta = seoConfig.articleMeta;
      pageSpecificSchema = {
        '@context': 'https://schema.org',
        '@type': meta.schemaType,
        headline: meta.headline,
        description: seoConfig.description,
        datePublished: meta.datePublished,
        dateModified: meta.datePublished,
        articleSection: meta.section,
        keywords: meta.keywords.join(', '),
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': canonicalUrl,
        },
        url: canonicalUrl,
        image: ogImage,
        author: {
          '@type': 'Organization',
          name: meta.authorName,
          url: SITE_URL,
        },
        publisher: {
          '@type': 'Organization',
          name: meta.publisherName,
          url: SITE_URL,
          logo: {
            '@type': 'ImageObject',
            url: DEFAULT_OG_IMAGE,
          },
        },
      };
    } else if (pathname === '/pulse') {
      pageSpecificSchema = {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: 'SNS Pulse',
        description: seoConfig.description,
        url: canonicalUrl,
        isPartOf: {
          '@type': 'WebSite',
          name: 'SNS Intelligence',
          url: SITE_URL,
        },
        blogPost: pulseArticlesSorted.map((article) => ({
          '@type': 'BlogPosting',
          headline: article.title,
          datePublished: article.date,
          url: `${SITE_URL}/pulse/${article.slug}`,
          description: article.excerpt,
        })),
      };
    } else if (pathname === '/press') {
      pageSpecificSchema = {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Press & Announcements',
        description: seoConfig.description,
        url: canonicalUrl,
        isPartOf: {
          '@type': 'WebSite',
          name: 'SNS Intelligence',
          url: SITE_URL,
        },
        hasPart: pressReleasesSorted.map((release) => ({
          '@type': 'NewsArticle',
          headline: release.title,
          datePublished: release.date,
          url: `${SITE_URL}/press/${release.slug}`,
          description: release.excerpt,
        })),
      };
    }
  }

  return (
    <Helmet>
      <title>{seoConfig.title}</title>
      <meta name="description" content={seoConfig.description} />

      <link rel="canonical" href={canonicalUrl} />

      {seoConfig.noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={seoConfig.title} />
      <meta property="og:description" content={seoConfig.description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="SNS Intelligence" />
      <meta property="og:site_name" content="SNS Intelligence" />

      {isArticlePage && seoConfig.articleMeta && (
        <meta property="article:published_time" content={seoConfig.articleMeta.datePublished} />
      )}
      {isArticlePage && seoConfig.articleMeta && (
        <meta property="article:section" content={seoConfig.articleMeta.section} />
      )}
      {isArticlePage &&
        seoConfig.articleMeta?.keywords.map((keyword) => (
          <meta key={keyword} property="article:tag" content={keyword} />
        ))}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={seoConfig.title} />
      <meta name="twitter:description" content={seoConfig.description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data */}
      {organizationSchema && (
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      )}
      {websiteSchema && (
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      )}
      {pageSpecificSchema && (
        <script type="application/ld+json">{JSON.stringify(pageSpecificSchema)}</script>
      )}
    </Helmet>
  );
};

export default Seo;
